import { supabase } from '../supabase/supabaseClient.js';

// Servicio especializado en jerarquía de datos
export const getCompanyHierarchy = async (companyId) => {
  try {
    // Obtener productos de la compañía usando la vista company_productos
    const { data: productos, error: productosError } = await supabase
      .from('company_productos')
      .select('*')
      .eq('compania_id', companyId);

    if (productosError) throw productosError;

    // Obtener planes relacionados a los productos usando la vista company_planes
    const { data: planes, error: planesError } = await supabase
      .from('company_planes')
      .select('*')
      .eq('compania_id', companyId);

    if (planesError) throw planesError;

    // Obtener sesiones usando la vista company_sesiones
    const { data: sesiones, error: sesionesError } = await supabase
      .from('company_sesiones')
      .select('*')
      .eq('compania_id', companyId);

    if (sesionesError) throw sesionesError;

    // Obtener cotizaciones usando la vista company_cotizaciones
    const { data: cotizaciones, error: cotizacionesError } = await supabase
      .from('company_cotizaciones')
      .select('*')
      .eq('compania_id', companyId);

    if (cotizacionesError) throw cotizacionesError;

    // Obtener contrataciones usando la vista company_contrataciones
    const { data: contrataciones, error: contratacionesError } = await supabase
      .from('company_contrataciones')
      .select('*')
      .eq('compania_id', companyId);

    if (contratacionesError) throw contratacionesError;

    // Obtener órdenes de pago usando la vista company_ordenes_pago
    const { data: ordenes_pago, error: ordenesPagoError } = await supabase
      .from('company_ordenes_pago')
      .select('*')
      .eq('compania_id', companyId);

    if (ordenesPagoError) throw ordenesPagoError;

    // Obtener pagos usando la vista company_pagos
    const { data: pagos, error: pagosError } = await supabase
      .from('company_pagos')
      .select('*')
      .eq('compania_id', companyId);

    if (pagosError) throw pagosError;

    // Obtener certificados usando la vista company_certificados
    const { data: certificados, error: certificadosError } = await supabase
      .from('company_certificados')
      .select('*')
      .eq('compania_id', companyId);

    if (certificadosError) throw certificadosError;

    // Obtener suscripciones usando la vista company_suscripciones
    const { data: subscriptions, error: subscriptionsError } = await supabase
      .from('company_suscripciones')
      .select('*')
      .eq('company_id', companyId);

    if (subscriptionsError) throw subscriptionsError;

    // Obtener cuotas usando la vista company_subscription_charges
    const { data: cuotas, error: cuotasError } = await supabase
      .from('company_subscription_charges')
      .select('*')
      .eq('company_id', companyId);

    if (cuotasError) throw cuotasError;

    const hierarchy = {
      productos: productos || [],
      planes: planes || [],
      sesiones: sesiones || [],
      simulaciones: sesiones?.filter(s => s.datos_simulacion) || [], // Simulaciones son sesiones con datos_simulacion
      cotizaciones: cotizaciones || [],
      contrataciones: contrataciones || [],
      ordenes_pago: ordenes_pago || [],
      pagos: pagos || [],
      certificados: certificados || [],
      subscriptions: subscriptions || [],
      cuotas: cuotas || []
    };

    return hierarchy;
  } catch (error) {
    console.error('Error fetching company hierarchy:', error);
    throw error;
  }
};

export const getDrillDownData = async (companyId, level, parentId = null) => {
  const levelQueries = {
    productos: () => supabase
      .from('company_productos')
      .select('*')
      .eq('compania_id', companyId),
    
    planes: () => supabase
      .from('company_planes')
      .select('*')
      .eq('compania_id', companyId)
      .eq('producto_id', parentId),
    
    sesiones: () => supabase
      .from('company_sesiones')
      .select('*')
      .eq('compania_id', companyId)
      .eq('plan_id', parentId),
    
    simulaciones: () => supabase
      .from('company_sesiones')
      .select('*')
      .eq('compania_id', companyId)
      .eq('id', parentId)
      .not('datos_simulacion', 'is', null),
    
    cotizaciones: () => {
      if (level === 'simulaciones') {
        return supabase
          .from('company_cotizaciones')
          .select('*')
          .eq('compania_id', companyId)
          .eq('sesion_id', parentId);
      }
      return supabase
        .from('company_cotizaciones')
        .select('*')
        .eq('compania_id', companyId)
        .eq('plan_id', parentId);
    },
    
    contrataciones: () => supabase
      .from('company_contrataciones')
      .select('*')
      .eq('compania_id', companyId)
      .eq('cotizacion_id', parentId),
    
    ordenes_pago: () => supabase
      .from('company_ordenes_pago')
      .select('*')
      .eq('compania_id', companyId)
      .eq('contratacion_id', parentId),
    
    pagos: () => supabase
      .from('company_pagos')
      .select('*')
      .eq('compania_id', companyId)
      .eq('orden_pago_id', parentId),
    
    certificados: () => supabase
      .from('company_certificados')
      .select('*')
      .eq('compania_id', companyId)
      .eq('pago_id', parentId),
    
    subscriptions: () => supabase
      .from('company_suscripciones')
      .select('*')
      .eq('company_id', companyId),
    
    cuotas: () => supabase
      .from('company_subscription_charges')
      .select('*')
      .eq('company_id', companyId)
      .eq('subscription_id', parentId)
  };

  try {
    const query = levelQueries[level];
    if (!query) {
      throw new Error(`Nivel no válido: ${level}`);
    }

    const { data, error } = await query();
    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error(`Error fetching drill down data for level ${level}:`, error);
    throw error;
  }
};

export default {
  getCompanyHierarchy,
  getDrillDownData
};